import { jsPDF } from "jspdf"

const PRIMARY_RGB = [85, 110, 230]
const MUTED_RGB = [108, 117, 125]
const DARK_RGB = [33, 37, 41]
const PAGE_MARGIN = 20

const formatDate = dateStr => {
  if (!dateStr) return "-"
  return new Date(dateStr).toLocaleDateString()
}

const formatMoney = (amount, currency = "SAR") => {
  const value = Number(amount || 0).toFixed(2)
  return `${currency} ${value}`
}

const capitalize = value => {
  if (!value) return "-"
  const str = String(value)
  return str.charAt(0).toUpperCase() + str.slice(1)
}

const resolveInvoicePaymentGateway = invoice => {
  const metadata = invoice?.metadata || {}
  if (
    metadata.assignmentMode === "manual_paid" ||
    metadata.manualPayment === true
  ) {
    return "manual"
  }
  return invoice?.paymentGateway || ""
}

const defaultLabels = {
  invoice: "Invoice",
  date: "Date",
  status: "Status",
  billTo: "Bill To",
  company: "Company",
  email: "Email",
  address: "Address",
  description: "Description",
  billingCycle: "Billing Cycle",
  period: "Period",
  amount: "Amount",
  total: "Total Amount",
  paymentGateway: "Payment Gateway",
  transactionId: "Transaction ID",
  thankYou: "Thank you for your business.",
}

/**
 * Build a printable invoice PDF from mapped invoice details (API or page state).
 */
export const generateInvoicePdf = (invoice, options = {}) => {
  const labels = { ...defaultLabels, ...options.labels }
  const issuerName = options.issuerName || "Wulooj"
  const doc = new jsPDF()
  const pageWidth = doc.internal.pageSize.getWidth()
  let y = PAGE_MARGIN

  const invoiceNumber = invoice.invoiceNumber || invoice._id || "-"
  const invoiceDate = formatDate(invoice.createdAt || invoice.date || invoice.issueDate)
  const status = capitalize(invoice.status)
  const currency = invoice.currency || "SAR"
  const company =
    invoice.billingInfo?.company || invoice.tenantName || "-"
  const email = invoice.billingInfo?.email || "-"
  const address = invoice.billingInfo?.address || ""

  doc.setFillColor(...PRIMARY_RGB)
  doc.rect(0, 0, pageWidth, 36, "F")
  doc.setTextColor(255, 255, 255)
  doc.setFontSize(22)
  doc.setFont("helvetica", "bold")
  doc.text(labels.invoice, PAGE_MARGIN, 22)
  doc.setFontSize(10)
  doc.setFont("helvetica", "normal")
  doc.text(issuerName, pageWidth - PAGE_MARGIN, 22, { align: "right" })

  y = 48
  doc.setTextColor(...DARK_RGB)
  doc.setFontSize(11)
  doc.setFont("helvetica", "bold")
  doc.text(`#${invoiceNumber}`, PAGE_MARGIN, y)

  doc.setFont("helvetica", "normal")
  doc.setFontSize(10)
  doc.setTextColor(...MUTED_RGB)
  y += 8
  doc.text(`${labels.date}: ${invoiceDate}`, PAGE_MARGIN, y)
  y += 6
  doc.text(`${labels.status}: ${status}`, PAGE_MARGIN, y)

  y += 14
  doc.setDrawColor(230, 230, 230)
  doc.line(PAGE_MARGIN, y, pageWidth - PAGE_MARGIN, y)
  y += 12

  doc.setTextColor(...DARK_RGB)
  doc.setFont("helvetica", "bold")
  doc.setFontSize(11)
  doc.text(labels.billTo, PAGE_MARGIN, y)
  y += 8
  doc.setFont("helvetica", "normal")
  doc.setFontSize(10)
  doc.text(`${labels.company}: ${company}`, PAGE_MARGIN, y)
  y += 6
  doc.text(`${labels.email}: ${email}`, PAGE_MARGIN, y)
  if (address) {
    y += 6
    doc.text(`${labels.address}: ${address}`, PAGE_MARGIN, y)
  }

  y += 16
  const tableX = PAGE_MARGIN
  const tableWidth = pageWidth - PAGE_MARGIN * 2
  const colWidths = [tableWidth * 0.5, tableWidth * 0.2, tableWidth * 0.3]
  const rowHeight = 10

  doc.setFillColor(245, 246, 248)
  doc.rect(tableX, y, tableWidth, rowHeight, "F")
  doc.setTextColor(...DARK_RGB)
  doc.setFont("helvetica", "bold")
  doc.setFontSize(9)
  doc.text(labels.description, tableX + 4, y + 7)
  doc.text(labels.billingCycle, tableX + colWidths[0] + 4, y + 7)
  doc.text(labels.amount, tableX + colWidths[0] + colWidths[1] + 4, y + 7)
  y += rowHeight

  const lineItems =
    Array.isArray(invoice.items) && invoice.items.length > 0
      ? invoice.items.map(item => ({
          description: item.description || invoice.planName || "-",
          billingCycle:
            invoice.billingCycle || invoice.plan?.billingCycle || "-",
          amount: formatMoney(item.total ?? item.unitPrice ?? invoice.amount, currency),
        }))
      : [
          {
            description: invoice.planName || invoice.plan?.name || labels.invoice,
            billingCycle: capitalize(invoice.billingCycle || invoice.plan?.billingCycle),
            amount: formatMoney(invoice.amount, currency),
          },
        ]

  doc.setFont("helvetica", "normal")
  lineItems.forEach(item => {
    doc.setDrawColor(240, 240, 240)
    doc.line(tableX, y + rowHeight, tableX + tableWidth, y + rowHeight)
    doc.text(String(item.description), tableX + 4, y + 7)
    doc.text(String(item.billingCycle), tableX + colWidths[0] + 4, y + 7)
    doc.text(String(item.amount), tableX + colWidths[0] + colWidths[1] + 4, y + 7)
    y += rowHeight
  })

  const periodStart = formatDate(invoice.periodStart)
  const periodEnd = formatDate(invoice.periodEnd)
  if (periodStart !== "-" || periodEnd !== "-") {
    y += 4
    doc.setTextColor(...MUTED_RGB)
    doc.setFontSize(9)
    doc.text(
      `${labels.period}: ${periodStart} – ${periodEnd}`,
      PAGE_MARGIN,
      y + 6
    )
    y += 10
  }

  y += 8
  doc.setDrawColor(...PRIMARY_RGB)
  doc.setLineWidth(0.5)
  doc.line(PAGE_MARGIN, y, pageWidth - PAGE_MARGIN, y)
  y += 12

  doc.setTextColor(...DARK_RGB)
  doc.setFont("helvetica", "bold")
  doc.setFontSize(12)
  doc.text(labels.total, PAGE_MARGIN, y)
  doc.setTextColor(...PRIMARY_RGB)
  doc.text(formatMoney(invoice.amount, currency), pageWidth - PAGE_MARGIN, y, {
    align: "right",
  })

  y += 18
  doc.setTextColor(...MUTED_RGB)
  doc.setFont("helvetica", "normal")
  doc.setFontSize(9)
  const paymentGateway = resolveInvoicePaymentGateway(invoice)
  if (paymentGateway) {
    doc.text(
      `${labels.paymentGateway}: ${capitalize(paymentGateway)}`,
      PAGE_MARGIN,
      y
    )
    y += 6
  }
  const txnId = invoice.transactionId || invoice.paymentId
  if (txnId) {
    doc.text(`${labels.transactionId}: ${txnId}`, PAGE_MARGIN, y)
    y += 6
  }

  y = doc.internal.pageSize.getHeight() - 20
  doc.setFontSize(8)
  doc.text(labels.thankYou, pageWidth / 2, y, { align: "center" })

  return doc
}

/**
 * Generate and trigger browser download of the invoice PDF.
 */
export const downloadInvoicePdf = (invoice, options = {}) => {
  const doc = generateInvoicePdf(invoice, options)
  const fileName = `${invoice.invoiceNumber || `invoice-${invoice._id}`}.pdf`
  doc.save(fileName)
}
